import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { motion, AnimatePresence } from 'framer-motion'
import { HeroBanner } from '../hero/hero-banner'
import { RoomSystemSection } from './room-system-section'
import { BookingSection } from './booking-section'
import { ReviewsSection } from './reviews-section'
import * as branchService from '@/services/branchService'
import * as roomService from '@/services/roomService'
import * as bookingService from '@/services/bookingService'
import * as settingsService from '@/services/settingsService'
import type { Branch } from '@/types/branch'
import type { RoomDetail } from '@/types/room'
import type { Booking, Room, RoomType } from '@/types/schedule'
import type { RoomCardProps } from '../rooms/room-card'
import { MapPin, Loader2, Images } from 'lucide-react'
import { GalleryGrid } from '../gallery-grid'
import { RoomSchedule } from '../schedule/room-schedule'
import { cn } from '@/lib/utils'
import { formatPrice, formatDateInput } from '@/utils/helpers'

// ─── Section IDs (dùng cho scroll spy) ───────────────────────────
export const SECTION_IDS = {
  branches: 'chi-nhanh',
  rooms: 'phong-nghi',
  gallery: 'thu-vien-anh',
  booking: 'dat-phong',
  reviews: 'danh-gia',
} as const

const SHOW_LOADING_ERROR_TOASTS = false

function reportLoadingError(message: string, error: unknown): void {
  console.warn(message, error)
  if (SHOW_LOADING_ERROR_TOASTS) toast.error(message)
}

// ─── Helpers ─────────────────────────────────────────────────────
function shortPrice(amount: number): string {
  if (amount >= 1000) {
    const k = amount / 1000
    return `${k % 1 === 0 ? k : k.toFixed(0)}K`
  }
  return formatPrice(amount)
}

function toRoomCardProps(room: RoomDetail): RoomCardProps {
  const parts: string[] = []
  if (room.combo3hRate > 0) parts.push(`3H/${shortPrice(room.combo3hRate)}`)
  else if (room.hourlyRate > 0) parts.push(`${shortPrice(room.hourlyRate)}/giờ`)
  if (room.combo6h1hRate > 0) parts.push(`6H+1H/${shortPrice(room.combo6h1hRate)}`)
  if (room.overnightRate > 0) parts.push(`Qua đêm/${shortPrice(room.overnightRate)}`)
  return {
    id: room.id,
    title: room.name,
    price: parts.join(' • ') || 'Liên hệ',
    images: room.images.map(roomService.imageUrl),
    type: room.type,
    discountSlots: room.discountSlots,
  }
}

// ─── Main Home Component ─────────────────────────────────────────
export const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Branch data
  const [branches, setBranches] = useState<Branch[]>([])
  const [branchLoading, setBranchLoading] = useState(true)

  // Room data
  const [rooms, setRooms] = useState<RoomDetail[]>([])
  const [roomActiveBranchId, setRoomActiveBranchId] = useState<string | null>(null)
  const [roomLoading, setRoomLoading] = useState(true)

  // Booking data
  const [date, setDate] = useState(new Date())
  const [allRooms, setAllRooms] = useState<Room[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [bookingActiveBranchId, setBookingActiveBranchId] = useState<string | null>(null)
  const [bookingLoading, setBookingLoading] = useState(true)
  const [focusedRoomId, setFocusedRoomId] = useState<string | null>(null)

  // ── Load branches ──
  useEffect(() => {
    branchService.getAll()
      .then(setBranches)
      .catch((error) => reportLoadingError('Không tải được danh sách chi nhánh', error))
      .finally(() => setBranchLoading(false))
  }, [])

  // ── Load rooms ──
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const [branchData, roomData] = await Promise.all([
          branchService.getAll(),
          roomService.getAll(),
        ])
        setRooms(roomData)
        const paramBranchId = searchParams.get('branchId')
        if (paramBranchId && branchData.some((b) => b.id === paramBranchId)) {
          setRoomActiveBranchId(paramBranchId)
        } else if (branchData.length > 0) {
          setRoomActiveBranchId(branchData[0].id)
        }
        if (paramBranchId) {
          const next = new URLSearchParams(searchParams)
          next.delete('branchId')
          setSearchParams(next, { replace: true })
        }
      } catch (error) {
        reportLoadingError('Không tải được dữ liệu phòng', error)
      } finally {
        setRoomLoading(false)
      }
    }
    fetchRooms()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load booking data ──
  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        const [branchData, roomData] = await Promise.all([
          branchService.getAll(),
          roomService.getAll(),
        ])
        const mappedRooms: Room[] = roomData.map((r) => ({
          id: r.id,
          name: r.name,
          type: r.type as RoomType,
          branchId: r.branchId,
          amenities: r.amenities || [],
          hourlyRate: r.hourlyRate,
          dailyRate: r.dailyRate,
          overnightRate: r.overnightRate,
          extraHourRate: r.extraHourRate,
          combo3hRate: r.combo3hRate,
          combo6h1hRate: r.combo6h1hRate,
          combo6h1hDiscount: r.combo6h1hDiscount,
          discountSlots: r.discountSlots,
          bookingMode: r.bookingMode,
        }))
        setAllRooms(mappedRooms)
        if (branchData.length > 0) {
          setBookingActiveBranchId(branchData[0].id)
        }
      } catch (error) {
        reportLoadingError('Không tải được dữ liệu đặt phòng', error)
      } finally {
        setBookingLoading(false)
      }
    }
    fetchBookingData()
  }, [])

  // ── Fetch bookings when date changes ──
  useEffect(() => {
    const dateStr = formatDateInput(date)
    let cancelled = false
    bookingService.getByDate(dateStr)
      .then((data) => { if (!cancelled) setBookings(data) })
      .catch((error) => {
        if (!cancelled) reportLoadingError('Không tải được lịch đặt phòng', error)
      })
    return () => { cancelled = true }
  }, [date])

  const handleBookingCreate = useCallback(async (newBooking: Omit<Booking, 'id'>) => {
    try {
      await bookingService.create(newBooking)
      const dateStr = formatDateInput(date)
      const data = await bookingService.getByDate(dateStr)
      setBookings(data)
      toast.success('Đặt phòng thành công')
    } catch (err) {
      toast.error('Đặt phòng thất bại, vui lòng thử lại')
      throw err
    }
  }, [date])

  // ── Filtered data ──
  const filteredRooms = useMemo(() => {
    if (!roomActiveBranchId) return []
    return rooms.filter((r) => r.branchId === roomActiveBranchId)
  }, [rooms, roomActiveBranchId])

  const roomItems: RoomCardProps[] = filteredRooms.map(toRoomCardProps)
  const roomActiveBranch = branches.find((b) => b.id === roomActiveBranchId)

  const bookingFilteredRooms = useMemo(() => {
    if (!bookingActiveBranchId) return []
    return allRooms.filter((r) => r.branchId === bookingActiveBranchId)
  }, [allRooms, bookingActiveBranchId])

  const handleBookingBranchChange = useCallback((branchId: string) => {
    setBookingActiveBranchId(branchId)
    setFocusedRoomId(null)
  }, [])

  return (
    <div className="flex flex-col gap-0">
      <HeroBanner />

      {/* ═══ Hệ thống phòng (UI mới — mock data) ═══ */}
      <RoomSystemSection id={SECTION_IDS.rooms} />

      {/* ═══ 4. Đặt phòng ═══ */}
      <BookingSection
        id={SECTION_IDS.booking}
        rooms={bookingFilteredRooms}
        bookings={bookings}
        scheduleDate={date}
        onDateChange={setDate}
        onBookingCreate={handleBookingCreate}
        focusedRoomId={focusedRoomId}
        onFocusChange={setFocusedRoomId}
      />

      {/* ═══ 5. Khách hàng nói gì về chúng tôi ═══ */}
      <ReviewsSection id={SECTION_IDS.reviews} />
    </div>
  )
}

// ─── Branch Card ─────────────────────────────────────────────────
import type { Variants } from 'framer-motion'

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
}

function BranchCard({ branch, onClick }: { branch: Branch; onClick: () => void }) {
  const images = (branch.images || []).map(branchService.imageUrl)
  const img = (i: number) => images[i] || images[0] || '/images/placeholder-room.png'
  const hasImages = images.length > 0

  return (
    <motion.div
      variants={cardVariants}
      onClick={onClick}
      className="flex flex-col gap-3 geometric-card bg-white p-3 shadow-sm cursor-pointer"
    >
      {hasImages ? (
        <div className="flex h-[200px] gap-2">
          <div className="relative flex-[5] overflow-hidden rounded-[2px]">
            <img src={img(0)} alt={branch.name} className="h-full w-full object-cover" loading="lazy" />
          </div>
          {images.length > 1 && (
            <div className="relative flex-[3] overflow-hidden rounded-[2px]">
              <img src={img(1)} alt={branch.name} className="h-full w-full object-cover" loading="lazy" />
            </div>
          )}
          {images.length > 2 && (
            <div className="flex flex-[2] flex-col gap-2">
              {[2, 3, 4].map((i) => images[i] ? (
                <div key={i} className="relative flex-1 overflow-hidden rounded-[2px]">
                  <img src={img(i)} alt={`${branch.name} ${i}`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                </div>
              ) : null)}
            </div>
          )}
        </div>
      ) : (
        <div className="flex h-[200px] items-center justify-center rounded-[2px] bg-gray-100">
          <MapPin className="size-12 text-gray-300" />
        </div>
      )}
      <div className="flex flex-col gap-0.5 px-1 pb-1">
        <h3 className="text-[17px] font-extrabold text-[#2B2B2B]">{branch.name}</h3>
        <p className="text-[13px] font-semibold text-[#6A635B]">{branch.address}</p>
      </div>
    </motion.div>
  )
}

// ─── Image Library Section ───────────────────────────────────────
function AlbumCell({
  album,
  className = '',
  textSize = 'sm',
  onClick,
}: {
  album: settingsService.GalleryAlbum
  className?: string
  textSize?: 'sm' | 'lg'
  onClick?: () => void
}) {
  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden rounded-[2px] border border-border/50 cursor-pointer group ${className}`}
    >
      <img
        src={settingsService.getFullImageUrl(album.cover)}
        alt={album.title}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-x-0 bottom-0 flex flex-col justify-end gap-0.5 bg-black/45 px-3 py-5 backdrop-blur-[1px]">
        <p className={`font-bold text-white leading-tight ${textSize === 'lg' ? 'text-2xl' : 'text-sm'}`}>
          {album.title}
        </p>
        <p className="flex items-center gap-1 text-[11px] font-medium text-white/80">
          <Images className="h-3 w-3" /> {album.images?.length || 0} ảnh
        </p>
      </div>
    </div>
  )
}

const STATIC_FALLBACK_ALBUMS: settingsService.GalleryAlbum[] = [
  { id: 'default-overall', title: 'Không gian tổng thể', cover: 'images/generated-1773763950437.png', images: [] },
  { id: 'default-cherry', title: 'Phòng Cherry & Pinterest', cover: 'images/generated-1773763964292.png', images: [] },
  { id: 'default-52hz', title: 'Phòng 52HZ', cover: 'images/generated-1773763985850.png', images: [] },
  { id: 'default-forest', title: "Phòng BB's Forest", cover: 'images/generated-1773763983771.png', images: [] },
  { id: 'default-canvas', title: "Phòng BB's Canvas & Coco", cover: 'images/generated-1773764044972.png', images: [] },
]

function ImageLibrarySection() {
  const navigate = useNavigate()
  const [albums, setAlbums] = useState<settingsService.GalleryAlbum[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const data = await settingsService.getGallery()
        setAlbums(data.length > 0 ? data : STATIC_FALLBACK_ALBUMS)
      } catch (err) {
        console.error("Failed to load gallery on homepage:", err)
        setAlbums(STATIC_FALLBACK_ALBUMS)
      } finally {
        setLoading(false)
      }
    }
    fetchGallery()
  }, [])

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-nhacam-primary" />
      </div>
    )
  }

  const homeAlbums = albums.filter((al) => al.showOnHome)
  const displayAlbums = homeAlbums.length > 0 ? homeAlbums : albums
  const [featured, ...rest] = displayAlbums

  const handleAlbumClick = () => {
    navigate('/thu-vien-anh')
  }

  return (
    <div className="mx-auto max-w-8xl px-4 sm:px-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#9B8B7A] mb-1">
            Thư viện ảnh
          </p>
          <h2 className="text-2xl font-extrabold text-[#2B2B2B] leading-tight sm:text-[36px]">
            Khám phá Nhà Cam
          </h2>
        </div>
      </div>

      {/* Mobile */}
      <div className="flex flex-col gap-3 sm:hidden">
        {featured && <AlbumCell album={featured} className="h-56" textSize="lg" onClick={handleAlbumClick} />}
        <div className="grid grid-cols-2 gap-3">
          {rest.map((album) => (
            <AlbumCell key={album.id} album={album} className="h-40" onClick={handleAlbumClick} />
          ))}
        </div>
      </div>

      {/* Desktop */}
      <div
        className="hidden sm:grid gap-3"
        style={{ gridTemplateColumns: '2fr 1fr 1fr', gridTemplateRows: '1fr 1fr', height: 500 }}
      >
        {featured && <AlbumCell album={featured} className="row-span-2" textSize="lg" onClick={handleAlbumClick} />}
        {rest.map((album) => (
          <AlbumCell key={album.id} album={album} onClick={handleAlbumClick} />
        ))}
      </div>
    </div>
  )
}

